import React from 'react'
import { isMobile } from 'react-device-detect'
import styled from 'styled-components'
import DcInput from '../dc-input/dc-input'
import { PixelFactoryContext } from '../pixel-factory/pixel-factory'
import DataTable, { createTheme } from '../ReactDataTable/index'
import './table.scss'

type TextSize = 'default' | 'small'

interface PixelTableProps {
  columns: any
  data: any
  progressPending?: boolean
  isSearchable?: boolean
  paginationPerPage?: number
  headerBackgroundColor?: string
  customStyles?: Object
  textSize?: TextSize
  expandableRows?: boolean
  expandableRowsComponent?: React.ComponentType<{ data: any }>
  expandableRowsHideExpander?: boolean
  expandableRowExpanded?: (row: any) => boolean
  expandOnRowClicked?: boolean
  onRowClicked?: (row: any) => void
  onRowExpandToggled?: (expanded: boolean, row: any) => void
  onChangePage?: (page: number) => void
  onChangeRowsPerPage?: (newPerPage: number) => void
  onSelectedRowsChange?: (state: any) => void
  selectableRows?: boolean
  selectableRowDisabled?: (row: any) => boolean
  selectableRowsHighlight?: boolean
  highlightOnHover?: boolean
  pointerOnHover?: boolean
  paginationRowsPerPageOptions?: number[]
  paginationTotalRows?: number
  paginationServer?: boolean
  pagination?: boolean
  clearSelectedRows?: boolean
  fixedHeader?: boolean
  fixedHeaderScrollHeight?: string
}

createTheme(
  'solarized',
  {
    text: {
      primary: '#000000',
      secondary: '#000000',
    },
    background: {
      default: '#ffffff',
    },
    context: {
      background: '#cb4b16',
      text: '#FFFFFF',
    },
    divider: {
      default: '#cecece',
    },
    action: {
      button: 'rgba(0,0,0,.54)',
      hover: 'rgba(0,0,0,.08)',
      disabled: 'rgba(0,0,0,.12)',
    },
  },
  'light',
)

const StyledBootstrapTable = styled(DataTable)`
  header {
    display: none !important;
  }
`

const StyledLoaderContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 500px;
`

const SearchPixelInput = styled.div<{ isMobile?: boolean }>`
  width: 30%;
  ${(props) => props.isMobile && 'width: 100%;'}
`

const CustomLoader = () => {
  const ref = React.useRef<HTMLDivElement>(null)
  const { showLoader, hideLoader } = React.useContext(PixelFactoryContext)

  React.useEffect(() => {
    if (ref.current) {
      showLoader(ref)
    }
    return () => {
      hideLoader(ref)
    }
  }, [])
  return (
    <React.Fragment>
      <StyledLoaderContainer className="pixeltable" ref={ref} />
    </React.Fragment>
  )
}

const customFilter = (val: string, data: any[]): any[] => {
  return data.filter((e) =>
    Object.values(e).some((v) => String(v).includes(val)),
  )
}

export const PixelTable = React.forwardRef<HTMLTableElement, PixelTableProps>(
  (
    {
      columns,
      paginationPerPage = 25,
      data = [],
      progressPending,
      isSearchable = false,
      headerBackgroundColor = '#ffffff',
      customStyles = {},
      textSize = 'default',
      expandableRows = false,
      expandableRowsComponent,
      expandableRowsHideExpander,
      expandableRowExpanded,
      expandOnRowClicked,
      onRowClicked,
      onRowExpandToggled,
      onChangePage,
      onChangeRowsPerPage,
      onSelectedRowsChange,
      selectableRows,
      highlightOnHover,
      pointerOnHover,
      paginationRowsPerPageOptions,
      paginationTotalRows,
      paginationServer,
      pagination,
      clearSelectedRows,
      fixedHeader,
      fixedHeaderScrollHeight,
      ...rest
    },
    ref,
  ) => {
    const [filterText, setFilterText] = React.useState('')
    const [resetPaginationToggle, setResetPaginationToggle] =
      React.useState(false)

    const subHeaderComponentMemo = React.useMemo(() => {
      const handleClear = () => {
        if (filterText) {
          setResetPaginationToggle(!resetPaginationToggle)
          setFilterText('')
        }
      }

      return (
        <SearchPixelInput isMobile={isMobile}>
          <DcInput
            inputSize={'small'}
            showsearchicon={0}
            placeholder="Search"
            name="search"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFilterText(e.target.value)
            }
            value={filterText}
          />
        </SearchPixelInput>
      )
    }, [filterText, resetPaginationToggle])

    const isSmallText = textSize === 'small'

    const defaultCustomStyles = {
      headCells: {
        style: {
          borderBottom: 'none',
          borderTop: 'none',
          backgroundColor: headerBackgroundColor,
          ...(isSmallText && {
            fontSize: '11px',
            padding: '4px 8px',
          }),
        },
      },
      cells: {
        style: {
          ...(isSmallText && {
            fontSize: '12px',
            padding: '4px 8px',
          }),
        },
      },
      rows: {
        style: {
          ...(isSmallText && {
            minHeight: '36px',
          }),
        },
      },
    }

    const tableProps = {
      data: customFilter(filterText, data),
      columns,
      keyField: 'id',
      pagination,
      paginationPerPage,
      responsive: true,
      subHeader: isSearchable,
      subHeaderComponent: subHeaderComponentMemo,
      progressPending,
      progressComponent: <CustomLoader />,
      customStyles: { ...defaultCustomStyles, ...customStyles },
      expandableRows,
      expandableRowsComponent,
      expandableRowsHideExpander,
      expandableRowExpanded,
      expandOnRowClicked,
      onRowClicked,
      onRowExpandToggled,
      onChangePage,
      onChangeRowsPerPage,
      onSelectedRowsChange,
      selectableRows,
      highlightOnHover,
      pointerOnHover,
      paginationRowsPerPageOptions,
      paginationTotalRows,
      paginationServer,
      clearSelectedRows,
      fixedHeader,
      fixedHeaderScrollHeight,
      ...rest,
    }

    return (
      <RdtClasses
        className={`pixeltable ${isMobile ? 'pixeltable--isMobile' : ''} ${
          isSmallText ? 'pixeltable--small-text' : ''
        }`}
      >
        <StyledBootstrapTable {...tableProps} />
      </RdtClasses>
    )
  },
)

const RdtClasses = styled.div`
  .rdt_TableHeadRow {
    border-bottom: none;
    width: 100%;
    > div {
      border-bottom: none;
      border-top: none;
    }
  }
  width: 100%;
`

export default PixelTable
